import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DefinitionCodeBlock from "./DefinitionCodeBlock";

interface DefinitionsDrawerProps {
  open: boolean;
  onClose: () => void;
  row: {
    destination_field_name?: string;
    source_field_expression?: string;
    filtering_definition?: Record<string, any>;
    unique_definition?: Record<string, any>;
  } | null;
}

const DRAWER_WIDTH = 500;

const DefinitionsDrawer: React.FC<DefinitionsDrawerProps> = ({
  open,
  onClose,
  row,
}) => {
  if (!row) return null;

  const { filtering_definition, unique_definition } = row || {};

  const filteringCode =
    filtering_definition && Object.keys(filtering_definition).length > 0
      ? JSON.stringify(filtering_definition, null, 2)
      : '{\n  "message": "No filtering definition available"\n}';

  const uniqueCode =
    unique_definition && Object.keys(unique_definition).length > 0
      ? JSON.stringify(unique_definition, null, 2)
      : '{\n  "message": "No unique definition available"\n}';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 200 }}
      PaperProps={{
        sx: {
          width: { xs: "94vw", sm: DRAWER_WIDTH },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: (theme) => theme.zIndex.appBar + 200,
        },
      }}
      ModalProps={{ keepMounted: false }}
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: "16px", lineHeight: 1.3 }}
          >
            Definitions
          </Typography>

          {row?.destination_field_name && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.25, fontSize: "12px" }}
            >
              {row?.destination_field_name}
            </Typography>
          )}

          {row?.source_field_expression && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "11px" }}
            >
              Affiliate Term: {row?.source_field_expression}
            </Typography>
          )}
        </Box>

        <IconButton size="small" onClick={onClose} aria-label="Close drawer">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {/* ── Body: Filtering first, Unique below ─────────────────── */}
      <Stack
        direction="column"
        spacing={2.5}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2.5,
          pt: 2.5,
          pb: 2.5,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { bgcolor: "#f0f0f0" },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#ccc",
            borderRadius: "3px",
          },
        }}
      >
        <DefinitionCodeBlock
          dataTracker="815"
          label="Filtering Definition"
          code={filteringCode}
        />
        <DefinitionCodeBlock
          dataTracker="816"
          label="Unique Definition"
          code={uniqueCode}
        />
      </Stack>
    </Drawer>
  );
};

export default DefinitionsDrawer;
